import { useCallback, useEffect, useRef } from "react";

type LongPressHandlers = {
  onPointerDown: (e: React.PointerEvent) => void;
  onPointerUp: () => void;
  onPointerCancel: () => void;
  onPointerMove: (e: React.PointerEvent) => void;
  onContextMenu: (e: React.MouseEvent) => void;
};

export function useLongPress(options: {
  enabled: boolean;
  onLongPress: () => void;
  /** default: 420 */
  thresholdMs?: number;
  /** default: 12 */
  maxMovePx?: number;
}): LongPressHandlers {
  const { enabled, onLongPress, thresholdMs = 420, maxMovePx = 12 } = options;
  const timerRef = useRef<number | null>(null);
  const startRef = useRef<{ x: number; y: number } | null>(null);
  const firedRef = useRef(false);

  const clear = useCallback(() => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    startRef.current = null;
  }, []);

  useEffect(() => clear, [clear]);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (!enabled) return;
      if (e.button !== 0) return;
      firedRef.current = false;
      startRef.current = { x: e.clientX, y: e.clientY };
      timerRef.current = window.setTimeout(() => {
        firedRef.current = true;
        onLongPress();
      }, thresholdMs);
    },
    [enabled, onLongPress, thresholdMs],
  );

  const onPointerUp = useCallback(() => {
    clear();
  }, [clear]);

  const onPointerCancel = useCallback(() => {
    clear();
  }, [clear]);

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!enabled) return;
      if (!startRef.current) return;
      const dx = Math.abs(e.clientX - startRef.current.x);
      const dy = Math.abs(e.clientY - startRef.current.y);
      if (dx > maxMovePx || dy > maxMovePx) {
        clear();
      }
    },
    [clear, enabled, maxMovePx],
  );

  const onContextMenu = useCallback(
    (e: React.MouseEvent) => {
      if (!enabled) return;
      if (!firedRef.current) return;
      e.preventDefault();
    },
    [enabled],
  );

  return { onPointerDown, onPointerUp, onPointerCancel, onPointerMove, onContextMenu };
}

